import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Check, Briefcase, Building2, KeyRound, Sofa, ShoppingBag, PencilRuler,
  HardHat, HeartHandshake, Truck, Trash2, PackageCheck, Wrench, Sparkles, Boxes,
  Recycle, LayoutGrid, Zap, Repeat, MessageCircle, Users, PackagePlus, Layers,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs, { toLdItems } from "@/components/Breadcrumbs";
import { SITE_URL, SERVICES, buildBreadcrumbLd } from "@/lib/seo";
import data from "@/data/professionnels.data.json";
import { useSeoT, PRO_I18N } from "@/lib/seoPagesI18n";
import { BUSINESS_I18N, SECTOR_LINKS } from "@/lib/businessI18n";

const SECTOR_ICONS = {
  regies: Building2, immobilier: KeyRound, meubles: Sofa, ecommerce: ShoppingBag,
  architectes: PencilRuler, entreprises: Briefcase, renovation: HardHat, ems: HeartHandshake,
};
const CATALOG_ICONS = {
  "demenagement": Truck, "debarras": Trash2, "transport-de-meubles": PackageCheck,
  "montage-de-meubles": Wrench, "nettoyage": Sparkles, "desencombrement": Boxes,
  "recyclage": Recycle, "home-organizing": LayoutGrid,
};
const WHY_ICONS = [Users, Zap, PackagePlus, MessageCircle, Layers, Check];
const H2 = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

export default function ProfessionnelsIndex() {
  const navigate = useNavigate();
  const { lang } = useSeoT();
  const b = BUSINESS_I18N[lang] || BUSINESS_I18N.fr;
  const bc = [{ name: "Accueil", path: "/" }, { name: b.hero.badge, path: "/professionnels" }];

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const goQuotePro = () => navigate("/devis?type=pro");

  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Service",
      name: "Mopper Business", description: b.seo.description,
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: { "@type": "AdministrativeArea", name: "Suisse romande" },
      serviceType: b.catalog.groups.map((g) => g.title),
      url: `${SITE_URL}/professionnels`,
    },
    buildBreadcrumbLd(toLdItems(bc)),
  ];

  return (
    <div className="min-h-screen bg-white text-[#0A0F2C]" data-testid="professionnels-index">
      <Seo title={b.seo.title} description={b.seo.description} path="/professionnels" jsonLd={jsonLd} />
      <Header />

      {/* HERO */}
      <section className="relative bg-[#0A0F2C] text-white overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24 px-5 sm:px-8 md:px-10 lg:px-14">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(45,91,255,0.45), transparent 55%)" }} />
        <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,216,77,0.10), transparent 70%)" }} />
        <div className="relative mx-auto" style={{ maxWidth: "1180px" }}>
          <div className="mb-6"><Breadcrumbs items={bc} variant="dark" /></div>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-[#FFD84D] font-bold mb-6">
            <Briefcase size={12} /> {b.hero.badge}
          </span>
          <h1 className="font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-4xl" style={H2}>{b.hero.h1}</h1>
          <p className="mt-6 text-white/85 text-lg md:text-xl max-w-2xl font-medium">{b.hero.sub}</p>
          <p className="mt-3 text-white/60 text-base max-w-2xl">{b.hero.text}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button onClick={goQuotePro} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD84D] hover:bg-yellow-300 text-[#0A0F2C] px-7 py-4 font-extrabold text-sm md:text-base transition-all hover:-translate-y-0.5" data-testid="biz-hero-cta-primary">
              {b.hero.ctaPrimary} <ArrowRight size={18} />
            </button>
            <a href="#externaliser" onClick={scrollTo("externaliser")} className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-white px-7 py-4 font-semibold text-sm md:text-base transition-all" data-testid="biz-hero-cta-secondary">
              {b.hero.ctaSecondary}
            </a>
          </div>
          <div className="mt-6 text-white/55 text-sm">{b.hero.reassure}</div>
        </div>
      </section>

      {/* DEUX FAÇONS DE TRAVAILLER */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-white" data-testid="biz-ways">
        <div className="mx-auto" style={{ maxWidth: "1180px" }}>
          <h2 className="font-extrabold text-3xl md:text-4xl tracking-tight max-w-2xl" style={H2}>{b.ways.title}</h2>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
            {b.ways.cards.map((c) => (
              <div key={c.key} className={`rounded-3xl p-7 md:p-9 border ${c.key === "clients" ? "bg-[#0A0F2C] text-white border-[#0A0F2C]" : "bg-[#F7F7F8] border-gray-100"}`} data-testid={`biz-way-${c.key}`}>
                <div className={`w-12 h-12 rounded-2xl inline-flex items-center justify-center ${c.key === "clients" ? "bg-[#FFD84D] text-[#0A0F2C]" : "bg-white text-[#2D5BFF] shadow-sm"}`}>
                  {c.key === "clients" ? <Users size={22} /> : <Building2 size={22} />}
                </div>
                <h3 className="mt-5 font-extrabold text-2xl tracking-tight" style={H2}>{c.title}</h3>
                <p className={`mt-3 text-[15px] leading-relaxed ${c.key === "clients" ? "text-white/80" : "text-[#4B5563]"}`}>{c.text}</p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5">
                  {c.items.map((it, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm">
                      <span className={`mt-1 inline-flex items-center justify-center w-4 h-4 rounded-full shrink-0 ${c.key === "clients" ? "bg-[#FFD84D] text-[#0A0F2C]" : "bg-[#2D5BFF] text-white"}`}><Check size={9} strokeWidth={3.5} /></span>
                      <span className={c.key === "clients" ? "text-white/90" : "text-[#0A0F2C]"}>{it}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={c.key === "clients" ? scrollTo("externaliser") : scrollTo("catalogue")}
                  className={`mt-7 inline-flex items-center gap-2 rounded-full px-6 py-3 font-bold text-sm transition-all hover:-translate-y-0.5 ${c.key === "clients" ? "bg-[#FFD84D] hover:bg-yellow-300 text-[#0A0F2C]" : "bg-[#2D5BFF] hover:bg-[#0A0F2C] text-white"}`}
                  data-testid={`biz-way-cta-${c.key}`}
                >
                  {c.cta} <ArrowRight size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VOUS VENDEZ / NOUS EXÉCUTONS */}
      <section id="externaliser" className="scroll-mt-24 py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#EAF0FF]" data-testid="biz-exec">
        <div className="mx-auto" style={{ maxWidth: "1180px" }}>
          <h2 className="font-extrabold text-3xl md:text-4xl tracking-tight max-w-3xl" style={H2}>{b.exec.title}</h2>
          <p className="mt-5 text-[#334155] text-base md:text-lg max-w-3xl leading-relaxed">{b.exec.text}</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {b.exec.models.map((m, i) => (
              <div key={i} className="bg-white rounded-3xl p-7 border border-white shadow-[0_2px_20px_rgba(10,15,44,0.05)]" data-testid={`biz-model-${i}`}>
                <div className="text-[#2D5BFF] font-extrabold text-3xl tabular-nums">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-3 font-extrabold text-lg" style={H2}>{m.t}</h3>
                <p className="mt-2 text-[15px] text-[#4B5563] leading-relaxed">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS PAR SECTEUR */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-white" data-testid="biz-sectors">
        <div className="mx-auto" style={{ maxWidth: "1180px" }}>
          <h2 className="font-extrabold text-3xl md:text-4xl tracking-tight" style={H2}>{b.sectors.title}</h2>
          <p className="mt-4 text-[#4B5563] text-base md:text-lg max-w-2xl">{b.sectors.subtitle}</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {b.sectors.items.map((s) => {
              const Ic = SECTOR_ICONS[s.key] || Briefcase;
              const href = SECTOR_LINKS[s.key] || "/devis?type=pro";
              return (
                <div key={s.key} className="group flex flex-col bg-[#F7F7F8] hover:bg-[#EAF0FF] rounded-3xl p-7 transition-colors border border-gray-100" data-testid={`biz-sector-${s.key}`}>
                  <div className="w-11 h-11 rounded-xl bg-white text-[#2D5BFF] inline-flex items-center justify-center shadow-sm"><Ic size={20} /></div>
                  <h3 className="mt-4 font-extrabold text-xl" style={H2}>{s.title}</h3>
                  <ul className="mt-4 flex flex-col gap-1.5 flex-1">
                    {s.cases.map((c, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#4B5563]"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#2D5BFF] shrink-0" />{c}</li>
                    ))}
                  </ul>
                  <Link to={href} className="mt-6 inline-flex items-center gap-1.5 text-[#2D5BFF] font-bold text-sm group-hover:gap-2.5 transition-all" data-testid={`biz-sector-cta-${s.key}`}>
                    {s.cta} <ArrowRight size={14} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CATALOGUE B2B */}
      <section id="catalogue" className="scroll-mt-24 py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#0A0F2C] text-white" data-testid="biz-catalog">
        <div className="mx-auto" style={{ maxWidth: "1180px" }}>
          <h2 className="font-extrabold text-3xl md:text-4xl tracking-tight max-w-2xl" style={H2}>{b.catalog.title}</h2>
          <p className="mt-4 text-white/70 text-base md:text-lg max-w-2xl">{b.catalog.subtitle}</p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {b.catalog.groups.map((g) => {
              const Ic = CATALOG_ICONS[g.key] || PackageCheck;
              const svc = SERVICES.find((x) => x.slug === g.key);
              return (
                <div key={g.key} className="flex flex-col bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 rounded-3xl p-6 transition-colors" data-testid={`biz-cat-${g.key}`}>
                  <div className="w-11 h-11 rounded-xl bg-[#2D5BFF] text-white inline-flex items-center justify-center"><Ic size={20} /></div>
                  <h3 className="mt-4 font-extrabold text-lg" style={H2}>{g.title}</h3>
                  <ul className="mt-3 flex flex-col gap-1 flex-1">
                    {g.items.map((it, i) => (<li key={i} className="text-[13px] text-white/65">{it}</li>))}
                  </ul>
                  {svc && (
                    <Link to={`/services/${g.key}`} className="mt-5 inline-flex items-center gap-1.5 text-[#FFD84D] font-bold text-sm hover:gap-2.5 transition-all" data-testid={`biz-cat-cta-${g.key}`}>
                      {b.catalog.cta} <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* POURQUOI EXTERNALISER */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-white" data-testid="biz-why">
        <div className="mx-auto" style={{ maxWidth: "1180px" }}>
          <h2 className="font-extrabold text-3xl md:text-4xl tracking-tight max-w-2xl" style={H2}>{b.why.title}</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {b.why.items.map((w, i) => {
              const Ic = WHY_ICONS[i % WHY_ICONS.length];
              return (
                <div key={i} className="bg-[#F7F7F8] rounded-2xl p-6 border border-gray-100" data-testid={`biz-why-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-[#EAF0FF] text-[#2D5BFF] inline-flex items-center justify-center"><Ic size={18} /></div>
                  <h3 className="mt-4 font-extrabold text-lg text-[#0A0F2C]" style={H2}>{w.t}</h3>
                  <p className="mt-2 text-[15px] text-[#4B5563] leading-relaxed">{w.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* EXEMPLES DE FONCTIONNEMENT */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#F7F7F8]" data-testid="biz-examples">
        <div className="mx-auto" style={{ maxWidth: "1180px" }}>
          <h2 className="font-extrabold text-3xl md:text-4xl tracking-tight max-w-3xl" style={H2}>{b.examples.title}</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {b.examples.items.map((ex, i) => (
              <div key={i} className="bg-white rounded-3xl p-7 border border-gray-100" data-testid={`biz-example-${i}`}>
                <h3 className="font-extrabold text-lg text-[#0A0F2C]" style={H2}>{ex.title}</h3>
                <ol className="mt-5 flex flex-col gap-3">
                  {ex.steps.map((st, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-[#334155]">
                      <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#EAF0FF] text-[#2D5BFF] font-extrabold text-xs shrink-0 tabular-nums">{j + 1}</span>
                      <span className="leading-relaxed">{st}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-[#6B7280] italic">{b.examples.note}</p>
        </div>
      </section>

      {/* PONCTUEL OU RÉCURRENT */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-white" data-testid="biz-freq">
        <div className="mx-auto" style={{ maxWidth: "1180px" }}>
          <h2 className="font-extrabold text-3xl md:text-4xl tracking-tight" style={H2}>{b.freq.title}</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-[#F7F7F8] rounded-3xl p-8 border border-gray-100" data-testid="biz-freq-ponctuel">
              <div className="w-11 h-11 rounded-xl bg-white text-[#2D5BFF] inline-flex items-center justify-center shadow-sm"><Zap size={20} /></div>
              <h3 className="mt-4 font-extrabold text-xl" style={H2}>{b.freq.ponctuel.t}</h3>
              <p className="mt-2 text-[15px] text-[#4B5563] leading-relaxed">{b.freq.ponctuel.d}</p>
            </div>
            <div className="bg-[#0A0F2C] text-white rounded-3xl p-8" data-testid="biz-freq-recurrent">
              <div className="w-11 h-11 rounded-xl bg-[#FFD84D] text-[#0A0F2C] inline-flex items-center justify-center"><Repeat size={20} /></div>
              <h3 className="mt-4 font-extrabold text-xl" style={H2}>{b.freq.recurrent.t}</h3>
              <p className="mt-2 text-[15px] text-white/80 leading-relaxed">{b.freq.recurrent.d}</p>
            </div>
          </div>
          <div className="mt-8">
            <button onClick={goQuotePro} className="inline-flex items-center gap-2 rounded-full bg-[#2D5BFF] hover:bg-[#0A0F2C] text-white px-7 py-4 font-extrabold text-sm transition-all hover:-translate-y-0.5" data-testid="biz-freq-cta">
              {b.freq.cta} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#2D5BFF] text-white" data-testid="biz-final-cta">
        <div className="mx-auto text-center" style={{ maxWidth: "820px" }}>
          <h2 className="font-extrabold text-3xl md:text-5xl tracking-tighter" style={H2}>{b.finalCta.title}</h2>
          <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed">{b.finalCta.text}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={goQuotePro} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD84D] hover:bg-yellow-300 text-[#0A0F2C] px-8 py-4 font-extrabold text-sm md:text-base transition-all hover:-translate-y-0.5" data-testid="biz-final-cta-primary">
              {b.finalCta.ctaPrimary} <ArrowRight size={18} />
            </button>
            <Link to="/devis" className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/25 text-white px-8 py-4 font-semibold text-sm md:text-base transition-all" data-testid="biz-final-cta-secondary">
              {b.finalCta.ctaSecondary}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
